/**
 * CSS优化工具
 * 提供合并和压缩CSS功能
 */

import { loadStyleAsync } from '../performance-optimization';

/**
 * 将多个CSS文件合并并添加到<head>
 * @param {Array<string>} stylesheets - CSS文件路径数组
 * @param {string} id - 生成的样式标签的ID
 */
export const combineStylesheets = (stylesheets, id = 'combined-styles') => {
  if (!stylesheets || !stylesheets.length) return;
  
  // 检查是否已存在合并样式
  const existingStylesheet = document.getElementById(id);
  if (existingStylesheet) return;
  
  Promise.all(
    stylesheets.map(stylesheet => 
      fetch(stylesheet).then(response => response.text())
    )
  )
  .then(cssContents => {
    // 合并所有CSS内容
    const combinedCSS = cssContents.join('\n');
    
    // 创建样式标签
    const styleElement = document.createElement('style');
    styleElement.id = id;
    styleElement.textContent = combinedCSS;
    
    // 添加到文档头部
    document.head.appendChild(styleElement);
    
    // 删除原始的样式表引用
    stylesheets.forEach(stylesheet => {
      const links = document.querySelectorAll(`link[href="${stylesheet}"]`);
      links.forEach(link => link.remove());
    });
  })
  .catch(error => {
    console.error('CSS文件合并失败:', error);
  });
};

/**
 * 压缩CSS字符串
 * @param {string} css - 要压缩的CSS字符串
 * @return {string} 压缩后的CSS
 */
export const minifyCSS = (css) => {
  if (!css) return '';
  
  return css
    // 删除注释
    .replace(/\/\*[\s\S]*?\*\//g, '')
    // 删除空格
    .replace(/\s+/g, ' ')
    // 删除各种空白字符
    .replace(/\s*([{}:;,])\s*/g, '$1')
    // 删除开始和结尾的空格
    .replace(/^\s+|\s+$/g, '')
    // 删除包围的分号
    .replace(/;\}/g, '}')
    // 压缩颜色值
    .replace(/#([0-9a-f])\1([0-9a-f])\2([0-9a-f])\3/gi, '#$1$2$3');
};

/**
 * 通过AJAX获取CSS文件内容，压缩并添加到DOM
 * @param {string} cssFile - CSS文件路径
 * @param {string} id - 生成的样式标签的ID
 */
export const loadAndMinifyCSS = (cssFile, id) => {
  if (!cssFile) return;
  
  fetch(cssFile)
    .then(response => response.text())
    .then(css => {
      const minifiedCSS = minifyCSS(css);
      
      // 创建样式标签
      const styleElement = document.createElement('style');
      if (id) styleElement.id = id;
      styleElement.textContent = minifiedCSS;
      
      // 添加到文档头部
      document.head.appendChild(styleElement);
      
      // 删除原始的样式表引用
      const links = document.querySelectorAll(`link[href="${cssFile}"]`);
      links.forEach(link => link.remove());
    })
    .catch(error => {
      console.error(`加载和压缩CSS文件 ${cssFile} 失败:`, error);
      // 失败时回退到原始加载方式
      loadStyleAsync(cssFile);
    });
};

/**
 * 对页面上引用的所有CSS进行即时添加和压缩
 */
export const optimizePageCSS = () => {
  // 获取所有CSS链接
  const cssLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
  
  // 将它们转换为内联样式
  cssLinks.forEach((link, index) => {
    const href = link.getAttribute('href');
    if (href) {
      // 只处理同源的样式表，避免CORS问题
      if (href.startsWith('/') || href.startsWith(window.location.origin)) {
        loadAndMinifyCSS(href, `inline-style-${index}`);
      }
    }
  });
};

/**
 * 批量处理并生成单个合并的CSS文件
 * @param {Array<string>} cssFiles - CSS文件路径数组
 * @return {Promise<string>} 合并并压缩后的CSS内容
 */
export const batchProcessCSS = (cssFiles) => {
  if (!cssFiles || !cssFiles.length) return Promise.resolve('');
  
  return Promise.all(
    cssFiles.map(file => 
      fetch(file).then(response => response.text())
    )
  )
  .then(cssContents => {
    // 合并所有CSS
    const combinedCSS = cssContents.join('\n');
    // 压缩合并后的CSS
    return minifyCSS(combinedCSS);
  });
};

/**
 * 对项目中的主要CSS文件进行优化
 */
export const optimizeProjectCSS = () => {
  // 项目主要CSS文件
  const mainStylesheets = [
    '/styles/index.css',
    '/pages/products/styles.css'
  ];
  
  // 组件特定的CSS文件
  const componentStylesheets = [
    '/components/admin/ArticleManagement.css'
  ];
  
  // 分别处理两组文件
  combineStylesheets(mainStylesheets, 'main-combined-styles');
  combineStylesheets(componentStylesheets, 'components-combined-styles');
};

export default {
  combineStylesheets,
  minifyCSS,
  loadAndMinifyCSS,
  optimizePageCSS,
  batchProcessCSS,
  optimizeProjectCSS
};
